import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { LogIn, LogOut, Package, Inbox, Settings, LayoutDashboard, ShieldAlert, Tags } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { ProductsTab } from "@/pages/admin/ProductsTab";
import { BrandsTab } from "@/pages/admin/BrandsTab";
import { InquiriesTab } from "@/pages/admin/InquiriesTab";
import { SettingsTab } from "@/pages/admin/SettingsTab";

const tabs = [["products", "Products", Package], ["brands", "Brands", Tags], ["inquiries", "Inquiries", Inbox], ["settings", "Settings", Settings]];

export default function AdminPage() {
  const { user, loading, login, logout } = useAuth();
  const location = useLocation();
  const [tab, setTab] = useState("products");
  const [stats, setStats] = useState(null);
  const isAdmin = user?.role === "admin";

  const loadStats = () => api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  useEffect(() => { if (isAdmin) loadStats(); }, [isAdmin, tab]);

  if (loading) return <div className="py-32 text-center text-slate-400" data-testid="admin-loading">Checking session…</div>;

  if (!user) return (
    <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="admin-login-page">
      <div className="sr-card p-10">
        <img src="/logo.jpg" alt="ShelfReady AI" className="h-10 mx-auto" />
        <p className="sr-label mt-8">Admin portal</p>
        <h1 className="mt-2 text-2xl font-extrabold uppercase text-[#0B1F4B]">Catalog editor</h1>
        <p className="mt-3 text-sm text-slate-500">Sign in with your Google account to manage products, pricing, inquiries and site settings.</p>
        {location.state?.authError && <p className="mt-3 text-sm text-[#E63946]" data-testid="admin-auth-error">Sign-in failed. Please try again.</p>}
        <button onClick={() => login("/admin")} className="sr-btn-blue mt-6 w-full justify-center" data-testid="admin-login-button"><LogIn size={16} /> Sign in with Google</button>
      </div>
    </div>
  );

  if (!isAdmin) return (
    <div className="max-w-md mx-auto px-4 py-24 text-center" data-testid="admin-forbidden">
      <div className="sr-card p-10">
        <ShieldAlert className="mx-auto text-[#E63946]" size={36} />
        <h1 className="mt-4 text-xl font-extrabold uppercase text-[#0B1F4B]">Not authorized</h1>
        <p className="mt-2 text-sm text-slate-500">{user.email} is not on the admin list. Ask the site owner to add your email.</p>
        <div className="mt-6 flex gap-2 justify-center">
          <Link to="/account" className="sr-btn-blue" data-testid="admin-go-to-account">My buyer account</Link>
          <button onClick={logout} className="sr-btn-outline" data-testid="admin-logout-button"><LogOut size={16} /> Sign out</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 lg:px-8 py-10" data-testid="admin-dashboard">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="sr-label flex items-center gap-2"><LayoutDashboard size={14} /> Admin dashboard</p>
          <h1 className="mt-2 text-3xl font-extrabold uppercase text-[#0B1F4B]">Catalog editor</h1>
        </div>
        <div className="flex items-center gap-3 text-sm">
          {user.picture && <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />}
          <span className="text-slate-600" data-testid="admin-user-email">{user.email}</span>
          <button onClick={logout} className="sr-btn-outline !py-2 !px-4" data-testid="admin-logout-button"><LogOut size={14} /> Sign out</button>
        </div>
      </div>

      {stats && (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="admin-stats">
          {[["Products", stats.products], ["Active", stats.active_products], ["Brands", stats.brands], ["Unpriced SKUs", stats.unpriced], ["Inquiries", stats.inquiries], ["New inquiries", stats.new_inquiries]].map(([k, v]) => (
            <div key={k} className="sr-card-tint p-4"><p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{k}</p><p className="text-2xl font-extrabold font-mono text-[#0B1F4B]">{v}</p></div>
          ))}
        </div>
      )}

      <div className="mt-8 flex gap-2 border-b border-slate-200">
        {tabs.map(([v, l, Icon]) => (
          <button key={v} onClick={() => setTab(v)} data-testid={`admin-tab-${v}`} className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === v ? "border-[#1E7CF2] text-[#1E7CF2]" : "border-transparent text-slate-500 hover:text-[#0B1F4B]"}`}><Icon size={16} /> {l}</button>
        ))}
      </div>
      <div className="mt-6">
        {tab === "products" && <ProductsTab onChange={loadStats} />}
        {tab === "brands" && <BrandsTab onChange={loadStats} />}
        {tab === "inquiries" && <InquiriesTab onChange={loadStats} />}
        {tab === "settings" && <SettingsTab />}
      </div>
    </div>
  );
}
